/**
 * An open report, in plain words: what to do next (never a bare "Do now"), how it is closed ("Mark it handled", formal record
 * wording), and the prompt when it has stayed open for hours. Shared by the phone and the web (owner, 2026-10-08: "do what now??").
 *
 * The next step comes from the report flow's own first steps (incidents.ts; reportflow.ts "Do this first"). The quick answers to
 * "What was done?" read like an incident record: past tense, third person, no food-safety claims; they are the person's own
 * statement of what was done, editable before saving.
 */
import { easyFor } from "./easy.ts";
import { incidentInfo, isPrivateType } from "./incidents.ts";
import type { Incident, IncidentType } from "./model.ts";

/** Formal one-tap answers to "What was done?", per report type. */
export const RESOLVE_CHIPS: Partial<Record<IncidentType, string[]>> = {
  fire: ["Fire extinguished; area inspected; no injuries reported.", "Fire department responded on site.", "False alarm; no fire found."],
  pest: ["Pest control company contacted and responded.", "Area cleaned; traps placed.", "Area re-inspected; no activity found."],
  temperature: ["Equipment adjusted; temperature re-checked.", "Affected food discarded.", "Equipment repaired by a service provider."],
  customer_illness_complaint: ["Complainant contacted; details recorded.", "Health department notified.", "No further complaints received."],
  vomit_diarrhea_cleanup: ["Area cleaned and disinfected.", "Affected food discarded.", "Area reopened after cleanup."],
  injury_cut_burn: ["First aid administered.", "Employee referred for medical care.", "Incident recorded; no further action needed."],
  equipment_failure: ["Equipment repaired by a service provider.", "Equipment replaced.", "Equipment removed from use pending repair."],
  water_power_outage: ["Water service restored.", "Power restored.", "Operations suspended until service was restored."],
  chemical_spill: ["Spill contained and cleaned up.", "Area ventilated and rinsed.", "Nearby food and supplies moved and inspected."],
  allergen_reaction: ["Guest attended to; no further medical care required.", "Emergency services responded on site.", "Dish removed from service pending review."],
  contamination: ["Affected food discarded.", "Area cleaned and sanitized.", "Gloves changed and hands washed."],
  mess: ["Area cleaned up.", "Wet floor sign placed."],
  other: ["Issue corrected.", "Reviewed; no action needed."],
};
const FALLBACK = ["Issue corrected.", "Reviewed; no action needed."];

/** Quick answers for this report type (none for a sick report: the person in charge records that decision, T4). */
export function resolveChips(t: IncidentType): string[] {
  if (isPrivateType(t)) return [];
  return RESOLVE_CHIPS[t] ?? FALLBACK;
}

export interface NextStep {
  /** The plain sentence under the title. */
  line: string;
  /** The steps button: "What to do in a fire", and the procedure it opens (the existing Easy steps). */
  steps?: { label: string; procedureId: string };
  /** A sick report is closed by the person in charge on their own screen, so no "Mark it handled". */
  canClose: boolean;
}

/** What to do now, for this type of open report, in plain words. */
export function nextStepFor(type: IncidentType): NextStep {
  if (isPrivateType(type)) return { line: "The person in charge decides what happens next.", canClose: false };
  const proc = easyFor({ incident: type });
  if (type === "fire") return { line: "Make sure everyone is safe. If it's out, check the area, then mark it handled.", canClose: true, ...(proc ? { steps: { label: "What to do in a fire", procedureId: proc.id } } : {}) };
  const first = incidentInfo(type).firstSteps[0] ?? "";
  const line = `${first ? `${first.replace(/\s+$/, "")} ` : ""}When it's taken care of, mark it handled.`;
  return { line, canClose: true, ...(proc ? { steps: { label: "See the steps", procedureId: proc.id } } : {}) };
}

/** How long an open report waits before we ask "Was it handled?". */
export const STILL_OPEN_HOURS = 3;

/** "10:49 PM" in the place's own clock. */
export function reportClock(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...(timeZone ? { timeZone } : {}) });
}

/** The prompt for a report still open after a few hours, else undefined. */
export function stillOpenLine(i: Pick<Incident, "status" | "reportedAt" | "type">, now: Date, timeZone?: string): string | undefined {
  if (i.status === "resolved" || isPrivateType(i.type)) return undefined;
  if (now.getTime() - Date.parse(i.reportedAt) < STILL_OPEN_HOURS * 3_600_000) return undefined;
  return `Still open since ${reportClock(i.reportedAt, timeZone)}. Was it handled?`;
}

export const YES_MARK_HANDLED = "Yes, mark it handled";
export const ASK_A_MANAGER_BUTTON = "Ask a manager";
export const ASK_A_MANAGER_LINE = "Ask a manager to mark it handled";

/** The history line once a report is closed: "Resolved by Zak at 9:14 pm: Fire extinguished; area inspected; no injuries reported." */
export function resolvedLine(i: Pick<Incident, "status" | "resolution" | "resolvedAt">, o: { by?: string; timeZone?: string }): string | undefined {
  if (i.status !== "resolved") return undefined;
  const at = i.resolvedAt ? ` at ${reportClock(i.resolvedAt, o.timeZone).replace(/\s?(AM|PM)$/, (_m, ap: string) => ` ${ap.toLowerCase()}`)}` : "";
  return `Resolved by ${o.by || "a manager"}${at}${i.resolution ? `: ${i.resolution}` : ""}`;
}
